import { IDENTIFIER_TYPES, NEW_NON_EMPLOYEE_KEYS } from './_const';

/**
 * extract an object from a larger parent object
 * @param {object} sourceObj parent object to pull member values from
 * @param {string} propName name of the property you desire
 * @param {object} primarySource optional object to pull from instead of `sourceObj`
 * @returns an object with only desired members
 */
export const extractValues = (sourceObj, propName, primarySource = null) => {
	const obj = {};
	Object.entries(sourceObj).forEach(([key, value]) => {
		if (!(key in obj)) {
			obj[key] =
				primarySource && key in primarySource
					? primarySource[key]
					: value[propName] ?? '';
		}
	});
	return obj;
};

/**
 * Sort by `label` member
 * @param {object} a object to sort by property in comparison to `b`
 * @param {object} b object to sort by property in comparison to `a`
 * @returns -1 if a.label < b.label, 0 if a.label=b.label, 1 if a.label > b.label
 */
export const sortByLabel = (a, b) => {
	if (a.label < b.label) return -1;
	if (a.label > b.label) return 1;
	return 0;
};

/**
 * Sort by `group` member
 * @param {object} a object to sort by property in comparison to `b`
 * @param {object} b object to sort by property in comparison to `a`
 * @returns -1 if a.group < b.group, 0 if a.group=b.group, 1 if a.group > b.group
 */
export const sortByGroup = (a, b) => {
	if (a.group < b.group) return -1;
	if (a.group > b.group) return 1;
	return 0;
};

export const getOldestValidDOB = asString => {
	let today = new Date();
	let minimumDOB = new Date(today.getFullYear() - 125, 0, 0, 0, 0, 0, 0);
	return asString ? getDateString(minimumDOB) : minimumDOB;
};

export const getYoungestValidDOB = asString => {
	let today = new Date();
	let maximumDOB = new Date(today.getFullYear() - 14, 0, 0, 0, 0, 0, 0);
	return asString ? getDateString(maximumDOB) : maximumDOB;
};

export const getDateString = d => {
	const year = d.getFullYear();
	const month = String(d.getMonth() + 1).padStart(2, '0');
	const day = String(d.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
};

export const extractIdentifierData = values => {
	let type = '';
	let specifier = '';
	let number = values[NEW_NON_EMPLOYEE_KEYS.IDENTIFIER_NUMBER];

	switch (values[NEW_NON_EMPLOYEE_KEYS.IDENTIFIER_TYPE]) {
		case IDENTIFIER_TYPES.SOCIAL_SECURITY_NUMBER:
			type = 'Social Security Number';
			break;
		case IDENTIFIER_TYPES.VISA:
			type = 'Visa Number';
			break;
		case IDENTIFIER_TYPES.PASSPORT:
			type = 'Passport Number';
			break;
		case IDENTIFIER_TYPES.DRIVERS_LICENSE:
			type = "Driver's License Number";
			specifier =
				values[NEW_NON_EMPLOYEE_KEYS.IDENTIFIER_SPECIFIER]
					?.stateAbbreviation;
			break;
		case IDENTIFIER_TYPES.EU_IDENTIFIER:
			type = 'EU Identifier Number';
			break;
		case IDENTIFIER_TYPES.MICROSOFT_ID:
			type = 'Microsoft ID';
			break;
		case IDENTIFIER_TYPES.UNIVERSITY_ID:
			type = 'University ID';
			specifier = values[NEW_NON_EMPLOYEE_KEYS.IDENTIFIER_SPECIFIER];
			break;
		case IDENTIFIER_TYPES.PARTNER_ID:
			type = 'Partner ID';
			specifier = values[NEW_NON_EMPLOYEE_KEYS.IDENTIFIER_SPECIFIER];
			break;
		case IDENTIFIER_TYPES.AADHAR_NUMBER:
			type = 'Aadhar Number';
			break;
		default:
			break;
	}
	return [type, number, specifier];
};

/**
 * special accommodation for drivers license where specifier is an object instead of typical string
 * @param {any} identifierSpecifier extra attribute to complement the identifier type (e.g., state for driver's license or university name for university ID)
 * @param {IDENTIFIER_TYPES} identifierType type of identifier selected
 * @returns {string} prepared identifier specifier value
 */
export const prepareIdentifierSpecifier = (
	identifierSpecifier,
	identifierType
) => {
	return typeof identifierSpecifier === 'string'
		? identifierSpecifier
		: typeof identifierSpecifier === 'object' &&
		  [IDENTIFIER_TYPES.DRIVERS_LICENSE].includes(identifierType)
		? identifierSpecifier?.stateAbbreviation
		: '';
};
